import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Table,
  Button,
  Space,
  Tag,
  Form,
  Input,
  Popconfirm,
  Upload,
  message,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import moment from "moment";

import {
  getHealthTypeListRequest,
  createHealthTypeRequest,
  updateHealthTypeRequest,
  deleteHealthTypeRequest,
  restoreHealthTypeRequest,
} from "providers/HealthTypeProvider/slice";
import Helper from "utils/helpers";
import "./styles.less";

const HealthTypeModal = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [iconUrl, setIconUrl] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);

  const { list, isLoading, total } = useSelector((state) => state.healthType);

  useEffect(() => {
    if (visible) {
      loadData();
    }
  }, [visible]);

  const loadData = () => {
    dispatch(getHealthTypeListRequest({
      limit: 100
    }));
  };

  const handleSubmit = async (values) => {
    const data = {
      title: values.title,
      description: values.description,
      icon: iconUrl || undefined,
      backgroundImage: backgroundUrl || undefined,
    };

    if (editingId) {
      // Update
      await dispatch(updateHealthTypeRequest({ _id: editingId, ...data }));
    } else {
      // Create
      await dispatch(createHealthTypeRequest(data));
    }

    handleCancelForm();
    // Reload data immediately after action
    loadData();
  };

  const handleEdit = (record) => {
    setEditingId(record._id);
    setIsFormVisible(true);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
    });

    // Set existing images
    setIconUrl(record.icon || null);
    setBackgroundUrl(record.backgroundImage || null);
  };

  const handleDelete = (id) => {
    dispatch(deleteHealthTypeRequest({ _id: id }));
    // Reload after delete
    setTimeout(() => loadData(), 500);
  };

  const handleRestore = (id) => {
    dispatch(restoreHealthTypeRequest({ _id: id }));
    // Reload after restore
    setTimeout(() => loadData(), 500);
  };

  const handleCancelForm = () => {
    setIsFormVisible(false);
    setEditingId(null);
    form.resetFields();
    setIconUrl(null);
    setBackgroundUrl(null);
  };

  // Custom upload handler for icon
  const handleUploadIcon = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploadingIcon(true);

    try {
      const uploadedImage = await Helper.uploadFile(
        { file, type: "AVATAR" },
        (progress) => {
          console.log("Upload progress:", progress);
        }
      );

      if (uploadedImage && uploadedImage.url) {
        setIconUrl(uploadedImage.url);
        onSuccess(uploadedImage, file);
        message.success("Icon uploaded successfully");
      } else {
        onError(new Error("Upload failed"));
        message.error("Failed to upload icon");
      }
    } catch (error) {
      onError(error);
      message.error("Upload error: " + error.message);
    } finally {
      setUploadingIcon(false);
    }
  };

  // Custom upload handler for background
  const handleUploadBackground = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploadingBg(true);

    try {
      const uploadedImage = await Helper.uploadFile(
        { file, type: "BANNER" },
        (progress) => {
          console.log("Upload progress:", progress);
        }
      );

      if (uploadedImage && uploadedImage.url) {
        setBackgroundUrl(uploadedImage.url);
        onSuccess(uploadedImage, file);
        message.success("Background uploaded successfully");
      } else {
        onError(new Error("Upload failed"));
        message.error("Failed to upload background");
      }
    } catch (error) {
      onError(error);
      message.error("Upload error: " + error.message);
    } finally {
      setUploadingBg(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "healthTypeId",
      key: "healthTypeId",
      width: 80,
      render: (id) => <span className="font-weight-bold">#{id || "N/A"}</span>,
    },
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      width: 80,
      align: "center",
      render: (icon) =>
        icon ? (
          <Avatar
            src={icon}
            size={40}
            shape="square"
            icon={<FileImageOutlined />}
            className="type-icon-avatar"
          />
        ) : (
          <Avatar
            size={40}
            shape="square"
            icon={<FileImageOutlined />}
            className="type-icon-placeholder"
          />
        ),
    },
    {
      title: "Tên loại",
      dataIndex: "title",
      key: "title",
      render: (title) => <strong>{title}</strong>,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc) => desc || <span className="text-muted">Chưa có mô tả</span>,
    },
    {
      title: "Số cơ sở",
      dataIndex: "healthPlaceCount",
      key: "healthPlaceCount",
      width: 100,
      align: "center",
      render: (count) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: "Trạng thái",
      dataIndex: "isDeleted",
      key: "isDeleted",
      width: 100,
      render: (isDeleted) => (
        <Tag color={isDeleted ? "red" : "green"}>
          {isDeleted ? "Đã xóa" : "Hoạt động"}
        </Tag>
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => date ? moment(date).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          {record.isDeleted ? (
            <Popconfirm
              title="Khôi phục loại y tế này?"
              onConfirm={() => handleRestore(record._id)}
              okText="Khôi phục"
              cancelText="Hủy"
            >
              <Button type="link" size="small" style={{ color: '#52c41a' }}>
                Khôi phục
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={
                <div>
                  <p>Bạn có chắc muốn xóa loại y tế này?</p>
                  {record.healthPlaceCount > 0 && (
                    <p style={{ color: '#ff4d4f', margin: 0 }}>
                      ⚠️ Có {record.healthPlaceCount} cơ sở đang sử dụng loại này
                    </p>
                  )}
                </div>
              }
              onConfirm={() => handleDelete(record._id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Xóa
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Quản lý loại y tế"
      visible={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      className="health-type-modal"
    >
      <div className="modal-header">
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsFormVisible(true)}
          >
            Thêm loại mới
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadData()} loading={isLoading}>
            Làm mới
          </Button>
        </Space>
      </div>

      {isFormVisible && (
        <div className="form-container">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="health-type-form"
          >
            <Form.Item
              name="title"
              label="Tên loại y tế"
              rules={[{ required: true, message: "Vui lòng nhập tên loại!" }]}
            >
              <Input placeholder="VD: Bệnh viện" />
            </Form.Item>

            <Form.Item name="description" label="Mô tả">
              <Input.TextArea
                rows={3}
                placeholder="Nhập mô tả chi tiết về loại y tế này..."
              />
            </Form.Item>

            <Space size="large">
              <Form.Item label="Icon">
                <Upload
                  customRequest={handleUploadIcon}
                  showUploadList={false}
                  accept="image/*"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingIcon}>
                    {uploadingIcon ? "Đang tải..." : "Chọn icon"}
                  </Button>
                </Upload>
                {iconUrl && (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={iconUrl}
                      alt="icon preview"
                      style={{ width: 60, height: 60, objectFit: "contain" }}
                    />
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() => setIconUrl(null)}
                    >
                      Xóa
                    </Button>
                  </div>
                )}
              </Form.Item>

              <Form.Item label="Ảnh nền">
                <Upload
                  customRequest={handleUploadBackground}
                  showUploadList={false}
                  accept="image/*"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingBg}>
                    {uploadingBg ? "Đang tải..." : "Chọn ảnh nền"}
                  </Button>
                </Upload>
                {backgroundUrl && (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={backgroundUrl}
                      alt="background preview"
                      style={{ width: 120, height: 80, objectFit: "cover" }}
                    />
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() => setBackgroundUrl(null)}
                    >
                      Xóa
                    </Button>
                  </div>
                )}
              </Form.Item>
            </Space>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingId ? "Cập nhật" : "Tạo mới"}
                </Button>
                <Button onClick={handleCancelForm}>Hủy</Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={list}
        loading={isLoading}
        rowKey="_id"
        pagination={{
          total,
          pageSize: 100,
          showTotal: (total) => `Tổng cộng: ${total} loại`,
        }}
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};

export default HealthTypeModal;
